import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HEALTH_PATH } from '../constants';
import { HealthCheckResponseDto } from '../models';
import { DebugService } from './debug.service';

/**
 * Service to track API server availability and health status with periodic health checks
 */
@Injectable({ providedIn: 'root' })
export class ApiHealthService {
  readonly #httpClient = inject(HttpClient);
  readonly #platformId = inject(PLATFORM_ID);
  readonly #debug = inject(DebugService);
  readonly #notification = inject(NzNotificationService);
  readonly #translate = inject(TranslateService);

  private readonly HEALTH_CHECK_INTERVAL_MS = 120000; // 2 minutes
  private healthCheckInterval?: ReturnType<typeof setInterval>;
  private hasShownUnavailableNotification = false;

  // Track if the API server is currently available
  readonly #isAvailableSource = signal(true);
  readonly isAvailable = this.#isAvailableSource.asReadonly();

  // Track the last error message
  readonly #lastErrorSource = signal<string | null>(null);
  readonly lastError = this.#lastErrorSource.asReadonly();

  // Store complete health check response
  readonly #healthDataSource = signal<HealthCheckResponseDto | null>(null);
  readonly healthData = this.#healthDataSource.asReadonly();

  // Track last successful check timestamp
  readonly #lastCheckTimestampSource = signal<Date | null>(null);
  readonly lastCheckTimestamp = this.#lastCheckTimestampSource.asReadonly();

  // Track if health monitoring is active
  readonly #isMonitoringSource = signal(false);
  readonly isMonitoring = this.#isMonitoringSource.asReadonly();

  /**
   * Start periodic health checks
   */
  startMonitoring(): void {
    if (!isPlatformBrowser(this.#platformId)) {
      return;
    }

    if (this.healthCheckInterval) {
      console.warn('[API Health] Monitoring already started');
      return;
    }

    this.#isMonitoringSource.set(true);

    // Perform initial health check immediately
    this.performHealthCheck();

    // Set up periodic checks
    this.healthCheckInterval = setInterval(() => {
      this.performHealthCheck();
    }, this.HEALTH_CHECK_INTERVAL_MS);

    if (!environment.production) {
      console.log('[API Health] Started monitoring (every 2 minutes)');
    }
  }

  /**
   * Stop periodic health checks
   */
  stopMonitoring(): void {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = undefined;
      this.#isMonitoringSource.set(false);

      if (!environment.production) {
        console.log('[API Health] Stopped monitoring');
      }
    }
  }

  /**
   * Perform a single health check
   */
  async performHealthCheck(): Promise<void> {
    try {
      const healthResponse = await firstValueFrom(this.#httpClient.get<HealthCheckResponseDto>(`${environment.API_URL}/${HEALTH_PATH}`));

      this.#healthDataSource.set(healthResponse);
      this.#lastCheckTimestampSource.set(new Date());

      // Update availability based on overall health status
      if (healthResponse.overall === 'healthy') {
        this.markAsAvailable();
      } else if (healthResponse.overall === 'degraded') {
        this.#isAvailableSource.set(true);
        this.#lastErrorSource.set('some-services-experiencing-issues');
        this.logServiceWarnings(healthResponse);
        this.showWarningNotification();
      } else {
        this.markAsUnavailable('api-server-unavailable');
        this.logServiceWarnings(healthResponse);
      }
    } catch (error) {
      this.markAsUnavailable('api-server-unavailable');
      this.#healthDataSource.set(null);

      if (!environment.production) {
        console.error('[API Health] Health check failed:', error);
      }
    }
  }

  /**
   * Log warnings for services with issues
   */
  private logServiceWarnings(health: HealthCheckResponseDto): void {
    if (!environment.production) {
      const { mongodb, redis } = health.services;

      if (mongodb.status !== 'connected') {
        console.warn(`[API Health] ⚠️ MongoDB is ${mongodb.status}`);
      }

      if (redis.status !== 'connected') {
        console.warn(`[API Health] ⚠️ Redis is ${redis.status}`);
        if (redis.details.lastError) {
          console.warn('[API Health] Redis error:', redis.details.lastError);
        }
      }

      if (redis.details.circuitBreakerOpen) {
        console.warn('[API Health] ⚠️ Redis circuit breaker is OPEN');
      }
    }
  }

  /**
   * Mark the API as unavailable with an optional error message
   * @param error - Translation key or error message
   */
  markAsUnavailable(error?: string): void {
    const wasAvailable = this.#isAvailableSource();
    this.#isAvailableSource.set(false);
    this.#lastErrorSource.set(error || 'api-server-unavailable');

    // Show notification only once when transitioning from available to unavailable
    if (wasAvailable && !this.hasShownUnavailableNotification && isPlatformBrowser(this.#platformId)) {
      this.showErrorNotification();
      this.hasShownUnavailableNotification = true;
    }
  }

  /**
   * Mark the API as available and clear any error messages
   */
  markAsAvailable(): void {
    const wasUnavailable = !this.#isAvailableSource();
    this.#isAvailableSource.set(true);
    this.#lastErrorSource.set(null);

    // Show recovery notification if it was previously unavailable
    if (wasUnavailable && this.hasShownUnavailableNotification && isPlatformBrowser(this.#platformId)) {
      this.showSuccessNotification();
      this.hasShownUnavailableNotification = false;
    }
  }

  /**
   * Reset the health status to the default state
   */
  reset(): void {
    this.markAsAvailable();
    this.#healthDataSource.set(null);
    this.#lastCheckTimestampSource.set(null);
  }

  /**
   * Get Redis cache hit rate as a number (0-100)
   */
  getRedisHitRate(): number | null {
    const health = this.#healthDataSource();
    if (!health?.services.redis.metrics) {
      return null;
    }

    const hitRate = health.services.redis.metrics.hitRate;
    return parseFloat(hitRate.replace('%', ''));
  }

  /**
   * Check if all services are healthy
   */
  areAllServicesHealthy(): boolean {
    const health = this.#healthDataSource();
    if (!health) {
      return false;
    }

    return health.overall === 'healthy' && health.services.mongodb.status === 'connected' && health.services.redis.status === 'connected';
  }

  /**
   * Show error notification when API becomes unavailable
   */
  private showErrorNotification(): void {
    const title = this.#translate.instant('errors.network.title') || 'Connection Error';
    const message = this.#translate.instant('api-server-unavailable');

    this.#notification.error(title, message, {
      nzPlacement: 'bottomRight',
      nzDuration: 0, // Don't auto-dismiss until resolved
      nzKey: 'api-health-error',
    });
  }

  /**
   * Show success notification when API recovers
   */
  private showSuccessNotification(): void {
    // First remove the error notification
    this.#notification.remove('api-health-error');

    const title = this.#translate.instant('connection-restored') || 'Connection Restored';
    const message = this.#translate.instant('api-server-available') || 'Successfully reconnected to the server';

    this.#notification.success(title, message, {
      nzPlacement: 'bottomRight',
      nzDuration: 5000,
    });
  }

  /**
   * Show warning notification for degraded services
   */
  private showWarningNotification(): void {
    const title = this.#translate.instant('service-degradation') || 'Service Issues';
    const message = this.#translate.instant('some-services-experiencing-issues');

    this.#notification.warning(title, message, {
      nzPlacement: 'bottomRight',
      nzDuration: 8000,
      nzKey: 'api-health-warning',
    });
  }
}
